import { useTranslation } from "react-i18next";
import { AnnouncementShell } from "./AnnouncementShell";
import { heroFor, type AnnouncementProps, type LocalizedHero } from "./types";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { openExternalUrl } from "../../tools/open-external-url";
import heroEn from "../../assets/images/announcements/healthy-hour-braf.en.webp";

/**
 * The Zoom registration page, through the source Zoom attributes to the in-app
 * banner. The campaign runs one registration link per channel, twenty in all,
 * so the debrief can say where each registrant came from. This is source 19,
 * "In-app banner". The push notification has its own (source 20), and swapping
 * one for the other would file the banner's registrations under the push.
 */
const REGISTRATION_URL =
  "https://us06web.zoom.us/webinar/register/7817883349190/WN_nNx-NWtURLm9uy8UL8hRFQ";

/**
 * The poster carries the date, the title, the two speakers and their portraits,
 * and it is in English for everyone: the session itself is held in English.
 * One artwork, then, and every other language falls back to it. The `alt`
 * repeats what the poster says, because the date is written nowhere else.
 */
const HERO: LocalizedHero = {
  en: heroEn,
  fr: null,
  it: null,
  de: null,
  es: null,
  pt: null,
  ar: null,
  nl: null,
};

/**
 * Healthy Hour #2, "BRAF V600E mCRC" — Figma AnZhfp8g9EzwryFt40m5yC 3362:3411.
 *
 * An invitation, not a feature tour: a poster, a chip, one heading, no feature
 * rows. Two buttons in the frame's own order, the way out first. "Later" is a
 * DISMISSAL like the feed sheet's "Not now", so it never counts as a conversion;
 * "Reserve your spot now" is the conversion, and it leaves the app for Zoom.
 *
 * The heading says 90 minutes where the frame says 60. Every other piece of the
 * campaign (the Zoom page, the invitation kit's 5:00 to 6:30 PM, the push copy)
 * says 90, and the campaign's own to-do list records a "60 → 90 MIN" fix on the
 * visuals, so the frame is the one that lagged.
 */
export function HealthyHourBrafAnnouncement({
  isOpen,
  onDismiss,
  onAct,
}: AnnouncementProps): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();

  return (
    <AnnouncementShell
      presentation="sheet"
      maxWidth="phone"
      isOpen={isOpen}
      onDismiss={onDismiss}
      media={
        <img
          src={heroFor(HERO, activeLang)}
          alt={t(
            "Juisci Healthy Hour webinar, Tuesday September 22 at 5:00 PM CEST, with Prof. Van K. Morris and Prof. David Tougeron"
          )}
          // No fixed height, unlike the two feature tours: this hero is a poster
          // with words on it, and `object-cover` at a fixed height crops the
          // logo and the last speaker line on any sheet wider than a phone (the
          // 600px tablet sheet, the desktop modal). The image is 1179×822, the
          // frame's 393×274 at 3x, so at its own ratio it lands on the design.
          className="block w-full"
        />
      }
      label={t("Webinar")}
      title={t(
        "90 minutes with Prof. Van K. Morris and Prof. David Tougeron on the latest first-line evidence in BRAF-mutated mCRC.\nLive in English."
      )}
      actions={[
        {
          label: t("Later"),
          variant: "outline",
          onPress: () => {
            onDismiss("decline_button");
          },
        },
        {
          label: t("Reserve your spot now"),
          variant: "healthy-hour",
          onPress: () => {
            onAct("register-webinar");
            openExternalUrl(REGISTRATION_URL);
          },
        },
      ]}
      features={null}
    />
  );
}
